// =====================================================================
// src/pages/intl-acca/DebriefPage.tsx
// =====================================================================
// Post-Tournament CLV Audit (PRD §9.2).
// Consumes the DebriefEnvelope returned by:
//   public.intl_acca_tournament_debrief(p_tournament_id uuid) → jsonb
//
// EVERY aggregate in `executive_summary` is nullable. This page is
// "wired but empty" until Pinnacle closes flow — that empty path is
// the most important one to render correctly.
//
// Routes mounted at: /intl-acca/debrief/:tournamentId
//
// Tier gating: free tier sees executive_summary only (per PRD §3.2);
// Sika Pro+ sees `leg_clv_table`, `best_markets`, `improvement_flags`.

import type {
  DebriefEnvelope,
  IntlAccaTournament,
  CLVConfidence,
  ResultStatus,
} from "../../db/intl-acca-types";
import { formatClvPp, formatOdds } from "../../db/intl-acca-types";
import { TournamentStateBanner } from "../../components/intl-acca/TournamentStateBanner";
import { DebriefMetricCard } from "../../components/intl-acca/DebriefMetricCard";
import { TierLockedTeaser } from "../../components/intl-acca/TierLockedTeaser";
import { HonestEmptyState } from "../../components/intl-acca/HonestEmptyState";
import { CLVConfidencePill } from "../../components/intl-acca/CLVConfidencePill";

export interface DebriefPageProps {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  envelope: DebriefEnvelope | null;
  /** Whether the current viewer's tier covers the full leg table. */
  canSeeFullAudit: boolean;
  onUpgrade?: () => void;
}

export function DebriefPage(props: DebriefPageProps): JSX.Element {
  const { loading, error, tournament, envelope, canSeeFullAudit, onUpgrade } = props;

  if (loading) {
    return (
      <div className="ia-root ia-page-pad" aria-busy="true">
        <div className="ia-eyebrow">Loading debrief…</div>
      </div>
    );
  }

  if (error || !envelope) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Debrief unavailable."
          body={error ?? "The RPC returned no envelope. Verify the tournament id."}
        />
      </div>
    );
  }

  if (envelope.ok === false) {
    return (
      <div className="ia-root ia-page-pad">
        <HonestEmptyState
          variant="custom"
          title="Tournament not found."
          body={`RPC error: ${envelope.error}`}
        />
      </div>
    );
  }

  const e = envelope.executive_summary;
  const noSettlement = e.legs_clv_audited === 0;

  return (
    <div className="ia-root">
      {tournament ? <TournamentStateBanner tournament={tournament} /> : null}

      <div className="ia-page-pad">
        {/* Header */}
        <header style={{ marginBottom: 32 }}>
          <div className="ia-eyebrow">{envelope.tournament} · Post-Tournament Debrief</div>
          <h1 className="ia-title" style={{ marginTop: 14 }}>
            The audit. <em>CLV first.</em>
          </h1>
          <p className="ia-sub" style={{ marginTop: 14 }}>
            Win rate is noise. CLV is signal (PRD §9). Every metric below is
            computed from real settled legs only — empty until the Pinnacle
            closing-line benchmark flows in.
          </p>
        </header>

        {/* EXECUTIVE SUMMARY (free tier visible) */}
        <section style={{ marginBottom: 40 }}>
          <div className="ia-eyebrow" style={{ marginBottom: 18 }}>
            Executive Summary
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 14,
            }}
          >
            <DebriefMetricCard
              label="Slips total"
              value={e.slips_total}
              format="count"
              sub={`${e.slips_settled} settled`}
            />
            <DebriefMetricCard
              label="Slip win rate"
              value={e.slip_win_rate}
              format="pct"
              sub="among settled"
              status={
                e.slip_win_rate === null ? null :
                e.slip_win_rate >= 0.55 ? "pos" : "warn"
              }
            />
            <DebriefMetricCard
              label="Legs CLV audited"
              value={e.legs_clv_audited}
              format="count"
              sub={`of ${e.legs_total} total`}
            />
            <DebriefMetricCard
              label="Avg leg CLV"
              value={e.avg_leg_clv_pp}
              format="pp"
              sub="vs Pinnacle close"
              status={
                e.avg_leg_clv_pp === null ? null :
                e.avg_leg_clv_pp > 0 ? "pos" : "neg"
              }
            />
            <DebriefMetricCard
              label="CLV+ rate"
              value={e.clv_positive_rate}
              format="pct"
              sub="target > 55%"
              status={
                e.clv_positive_rate === null ? null :
                e.clv_positive_rate >= 0.55 ? "pos" : "warn"
              }
            />
            <DebriefMetricCard
              label="Total implied edge"
              value={e.total_implied_edge}
              format="pp"
              sub="sum across legs"
            />
          </div>
        </section>

        {/* Engine-validation default: nothing settled yet */}
        {noSettlement ? (
          <HonestEmptyState variant="no-clv-yet" />
        ) : (
          <>
            {/* Detailed leg table — Sika Pro+ only */}
            <section style={{ marginBottom: 40 }}>
              <div className="ia-eyebrow" style={{ marginBottom: 18 }}>
                Leg-by-Leg CLV Audit · {envelope.leg_clv_table.length} legs
              </div>
              {canSeeFullAudit ? (
                <LegCLVTable rows={envelope.leg_clv_table} />
              ) : (
                <TierLockedTeaser gate="debrief_full" onUpgrade={onUpgrade}>
                  <LegCLVTable rows={envelope.leg_clv_table.slice(0, 3)} />
                </TierLockedTeaser>
              )}
            </section>

            {/* Best & worst markets */}
            <section style={{ marginBottom: 40, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              <MarketList
                eyebrow="Best Performing · CLV+"
                rows={envelope.best_markets}
                positive
              />
              <MarketList
                eyebrow="Improvement Flags · CLV−"
                rows={envelope.improvement_flags}
              />
            </section>

            <p
              className="ia-mono"
              style={{
                marginTop: 32,
                fontSize: "var(--sz-mono-xs)",
                color: "var(--text-tertiary)",
                letterSpacing: "0.18em",
              }}
            >
              {envelope.model_accuracy_note} · PDF export: {envelope.pdf_export}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── leg CLV table ────────────────────────────────────────────────
function LegCLVTable({
  rows,
}: {
  rows: Array<{
    match_id: string;
    selection: string;
    strikza_price: number;
    market_price_at_selection: number;
    closing_price: number | null;
    clv_pp: number | null;
    clv_confidence: CLVConfidence;
    result: ResultStatus | null;
  }>;
}): JSX.Element {
  if (rows.length === 0) {
    return <HonestEmptyState variant="tournament-empty" />;
  }
  return (
    <div className="ia-card" style={{ overflow: "hidden" }}>
      {rows.map((row, i) => (
        <div
          key={`${row.match_id}-${i}`}
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 80px 80px 80px 120px",
            gap: 12,
            alignItems: "center",
            padding: "12px 18px",
            borderTop: i > 0 ? "1px solid var(--border-faint)" : "none",
          }}
        >
          <span className="ia-mono" style={{ fontSize: "var(--sz-mono-sm)", color: "var(--text-tertiary)" }}>
            {row.match_id}
          </span>
          <span style={{ color: "var(--accent)", fontWeight: 500 }}>
            {row.selection}
          </span>
          <span className="ia-mono" style={{ textAlign: "right" }}>{formatOdds(row.strikza_price)}</span>
          <span className="ia-mono" style={{ textAlign: "right", color: "var(--text-secondary)" }}>
            {formatOdds(row.market_price_at_selection)}
          </span>
          <span className="ia-mono" style={{ textAlign: "right", color: "var(--text-secondary)" }}>
            {row.closing_price === null ? "—" : formatOdds(row.closing_price)}
          </span>
          <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8 }}>
            <CLVConfidencePill confidence={row.clv_confidence} clv_pp={row.clv_pp} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── market lists ─────────────────────────────────────────────────
function MarketList({
  eyebrow,
  rows,
  positive,
}: {
  eyebrow: string;
  rows: Array<{ selection_pattern?: string; selection?: string; n: number; avg_clv_pp: number | null }>;
  positive?: boolean;
}): JSX.Element {
  return (
    <div className="ia-card" style={{ padding: 20 }}>
      <div className="ia-eyebrow">{eyebrow}</div>
      {rows.length === 0 ? (
        <p
          className="ia-empty-body"
          style={{ marginTop: 16, color: "var(--text-tertiary)", textAlign: "left" }}
        >
          No qualifying rows yet.
        </p>
      ) : (
        <div style={{ marginTop: 14 }}>
          {rows.map((row, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 50px 80px",
                gap: 12,
                alignItems: "center",
                padding: "10px 0",
                borderBottom: i < rows.length - 1 ? "1px solid var(--border-faint)" : "none",
              }}
            >
              <span style={{ fontSize: "var(--sz-body-sm)", color: "var(--text)" }}>
                {row.selection_pattern ?? row.selection}
              </span>
              <span
                className="ia-mono"
                style={{ fontSize: "var(--sz-mono-sm)", color: "var(--text-tertiary)", textAlign: "right" }}
              >
                n={row.n}
              </span>
              <span
                className="ia-mono"
                style={{
                  fontSize: "var(--sz-mono-md)",
                  color: positive ? "var(--pos)" : "var(--neg)",
                  textAlign: "right",
                }}
              >
                {formatClvPp(row.avg_clv_pp)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// =====================================================================
// DATA BINDING CONTRACT
// =====================================================================
//
// const { data: envelope } = await supabase.rpc(
//   'intl_acca_tournament_debrief',
//   { p_tournament_id: tournamentId }
// );
//
// const { data: tournament } = await supabase.from('intl_acca_tournaments')
//                                    .select('*').eq('id', tournamentId).single();
//
// const canSeeFullAudit = tierMeets(currentTier, 'sika_pro');
